import React, { useEffect, useState } from "react";

export default function NotFoundPage() {
  const [glitchActive, setGlitchActive] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), 220);
    }, 3200);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="nf-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600&family=Baloo+2:wght@600;700&display=swap');

        :root {
          --primary-color: #f2f2f8;
          --secondary-color: rgb(13, 103, 133);
          --tertiary-color: #022d3a;
          --font-primary: "Poppins", sans-serif;
          --font-secondary: "Baloo 2", cursive;
        }

        * { box-sizing: border-box; }

        .nf-root {
          min-height: 100vh;
          width: 100%;
          background: radial-gradient(circle at 50% 30%, #ffffff 0%, var(--primary-color) 60%, color-mix(in srgb, var(--primary-color), #000 4%) 100%);
          color: var(--tertiary-color);
          font-family: var(--font-primary);
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
          padding: 24px;
        }

        /* Faint scanline overlay */
        .nf-root::before {
          content: "";
          position: absolute;
          inset: 0;
          background: repeating-linear-gradient(
            to bottom,
            color-mix(in srgb, var(--tertiary-color), transparent 98.5%) 0px,
            color-mix(in srgb, var(--tertiary-color), transparent 98.5%) 1px,
            transparent 1px,
            transparent 3px
          );
          pointer-events: none;
          z-index: 5;
        }

        .nf-content {
          position: relative;
          z-index: 6;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          max-width: 640px;
          width: 100%;
        }

        .nf-label {
          font-family: var(--font-primary);
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: color-mix(in srgb, var(--tertiary-color), transparent 55%);
          margin-bottom: 18px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .nf-label .dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--secondary-color);
          box-shadow: 0 0 8px color-mix(in srgb, var(--secondary-color), transparent 40%);
          animation: nf-blink 1.6s ease-in-out infinite;
        }

        @keyframes nf-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }

        /* Radar */
        .nf-radar {
          width: 108px;
          height: 108px;
          border-radius: 50%;
          border: 1px solid color-mix(in srgb, var(--secondary-color), transparent 78%);
          position: relative;
          margin-bottom: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: color-mix(in srgb, var(--secondary-color), transparent 97%);
        }

        .nf-radar::before {
          content: "";
          position: absolute;
          width: 66%;
          height: 66%;
          border-radius: 50%;
          border: 1px solid color-mix(in srgb, var(--secondary-color), transparent 84%);
        }

        .nf-radar::after {
          content: "";
          position: absolute;
          width: 32%;
          height: 32%;
          border-radius: 50%;
          border: 1px solid color-mix(in srgb, var(--secondary-color), transparent 84%);
        }

        .nf-radar-sweep {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: conic-gradient(from 0deg, color-mix(in srgb, var(--secondary-color), transparent 65%), transparent 28%);
          animation: nf-spin 2.6s linear infinite;
        }

        .nf-radar-core {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--secondary-color);
          box-shadow: 0 0 10px 2px color-mix(in srgb, var(--secondary-color), transparent 50%);
          z-index: 2;
        }

        @keyframes nf-spin {
          to { transform: rotate(360deg); }
        }

        /* 404 glitch text */
        .nf-code {
          font-family: var(--font-secondary);
          font-weight: 700;
          font-size: clamp(72px, 18vw, 148px);
          line-height: 1;
          letter-spacing: -0.01em;
          position: relative;
          color: var(--secondary-color);
          margin: 0 0 4px 0;
        }

        .nf-code span {
          position: relative;
          display: inline-block;
        }

        .nf-code.glitch span {
          animation: nf-shake 0.22s linear;
        }

        .nf-code.glitch::before,
        .nf-code.glitch::after {
          content: "404";
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 100%;
        }

        .nf-code.glitch::before {
          color: var(--tertiary-color);
          mix-blend-mode: multiply;
          animation: nf-glitch-1 0.22s linear;
        }

        .nf-code.glitch::after {
          color: color-mix(in srgb, var(--secondary-color), #ffffff 35%);
          mix-blend-mode: multiply;
          animation: nf-glitch-2 0.22s linear;
        }

        @keyframes nf-glitch-1 {
          0% { clip-path: inset(0 0 80% 0); transform: translate(calc(-50% + 3px), -2px); }
          40% { clip-path: inset(60% 0 5% 0); transform: translate(calc(-50% - 4px), 2px); }
          80% { clip-path: inset(20% 0 60% 0); transform: translate(calc(-50% + 2px), 0); }
          100% { clip-path: inset(0 0 0 0); transform: translate(-50%, 0); }
        }

        @keyframes nf-glitch-2 {
          0% { clip-path: inset(70% 0 5% 0); transform: translate(calc(-50% - 3px), 2px); }
          40% { clip-path: inset(10% 0 70% 0); transform: translate(calc(-50% + 4px), -1px); }
          80% { clip-path: inset(45% 0 20% 0); transform: translate(calc(-50% - 2px), 0); }
          100% { clip-path: inset(0 0 0 0); transform: translate(-50%, 0); }
        }

        @keyframes nf-shake {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(-2px, 1px); }
          50% { transform: translate(2px, -1px); }
          75% { transform: translate(-1px, -1px); }
        }

        .nf-headline {
          font-family: var(--font-secondary);
          font-weight: 600;
          font-size: clamp(18px, 3vw, 22px);
          color: var(--secondary-color);
          margin: 6px 0 10px 0;
        }

        .nf-desc {
          font-family: var(--font-primary);
          font-size: 15px;
          line-height: 1.6;
          color: color-mix(in srgb, var(--tertiary-color), transparent 25%);
          max-width: 420px;
          margin: 0 0 34px 0;
        }

        .nf-coords {
          font-family: var(--font-primary);
          font-size: 11px;
          letter-spacing: 0.05em;
          color: color-mix(in srgb, var(--tertiary-color), transparent 65%);
        }

        .nf-actions {
          display: flex;
          gap: 14px;
          flex-wrap: wrap;
          justify-content: center;
          margin-bottom: 30px;
        }

        .nf-btn {
          font-family: var(--font-primary);
          font-size: 14px;
          font-weight: 600;
          padding: 13px 26px;
          border-radius: 8px;
          cursor: pointer;
          border: 1px solid transparent;
          transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease;
        }

        .nf-btn:focus-visible {
          outline: 2px solid var(--secondary-color);
          outline-offset: 3px;
        }

        .nf-btn-primary {
          background: var(--secondary-color);
          color: #ffffff;
        }

        .nf-btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 26px -10px color-mix(in srgb, var(--secondary-color), transparent 40%);
        }

        .nf-btn-ghost {
          background: #ffffff;
          color: var(--tertiary-color);
          border-color: color-mix(in srgb, var(--tertiary-color), transparent 82%);
        }

        .nf-btn-ghost:hover {
          border-color: color-mix(in srgb, var(--tertiary-color), transparent 65%);
          transform: translateY(-2px);
        }

        @media (max-width: 480px) {
          .nf-radar { width: 84px; height: 84px; margin-bottom: 22px; }
          .nf-desc { font-size: 14px; }
          .nf-actions { flex-direction: column; width: 100%; }
          .nf-btn { width: 100%; text-align: center; }
        }

        @media (prefers-reduced-motion: reduce) {
          .nf-radar-sweep, .nf-label .dot, .nf-code.glitch::before, .nf-code.glitch::after, .nf-code.glitch span {
            animation: none !important;
          }
        }
      `}</style>

      <div className="nf-content">
        <div className="nf-label">
          <span className="dot" />
          SIGNAL LOST
        </div>

        <div className="nf-radar" aria-hidden="true">
          <div className="nf-radar-sweep" />
          <div className="nf-radar-core" />
        </div>

        <h1 className={`nf-code${glitchActive ? " glitch" : ""}`}>
          <span>404</span>
        </h1>

        <p className="nf-headline">This page drifted out of range.</p>
        <p className="nf-desc">
          The link you followed doesn't match anything we're broadcasting.
          It may have moved, changed its name, or never existed on this frequency.
        </p>

        <div className="nf-actions">
          <button
            className="nf-btn nf-btn-primary"
            onClick={() => (window.location.href = "/")}
          >
            Return to homepage
          </button>
          <button
            className="nf-btn nf-btn-ghost"
            onClick={() => window.history.back()}
          >
            Go back
          </button>
        </div>

        <p className="nf-coords">ERR_PAGE_NOT_FOUND · 40.7128° N, 74.0060° W</p>
      </div>
    </div>
  );
}